import type { LiveQuoteReloadFormResponse } from 'apiConnector/secondary-market/live/models';
import type { ClientOrRelativesResponse, NewQuoteSalesCounterpart } from 'apiConnector/secondary-market/shared/clients';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { Action } from 'state/actions';
import type { OrderActionType } from 'state/secondaryMarket/shared/actions';
import type { Defaults, DefaultsMap } from 'state/secondaryMarket/shared/defaults/defaults.model';
import { initialDefaultsMap } from 'state/secondaryMarket/shared/defaults/defaults.model';
import { addOrPatchKey, addOrReplaceKey } from 'state/utils/collection';

type DefaultActionType = Exclude<OrderActionType, 'CP_ORDER'>;
export function defaultReducerFactory(actionType: DefaultActionType) {
  return (state: DefaultsMap = initialDefaultsMap(), action: Action): DefaultsMap => {
    switch (action.type) {
      case `SECONDARY_MARKET_${actionType}_INIT_FORM_FETCHED` as const:
      case `SECONDARY_MARKET_${actionType}_RELOAD_FORM_FETCHED` as const:
        const defaults = mapInitResponse(action.response ?? undefined);
        if (defaults !== undefined) {
          return addOrReplaceKey(state, action.tabId, defaults);
        }
        return state;
      case `SECONDARY_MARKET_${actionType}_CLIENT_RELATED_RECEIVED` as const:
        return addOrPatchKey(state, action.tabId, () => mapClientResponse(action.response) ?? {});
      case `SECONDARY_MARKET_${actionType}_CLIENT_RELATED_FAILED` as const:
        return addOrPatchKey(state, action.tabId, () => ({
          salesId: undefined,
        }));
      default:
        return state;
    }
  };
}

function mapInitResponse(response: LiveQuoteReloadFormResponse | undefined): Defaults | undefined {
  const defaulting = response?.input;
  if (defaulting === undefined) {
    return undefined;
  }
  return {
    client: defaulting?.client,
    salesId: defaulting?.salesId ?? undefined,
    settlementDate: defaulting?.settlementDate ?? undefined,
    tradeWay: defaulting?.tradeWay ?? undefined,
  };
}

function mapClientSalesCounterpart(client: NewQuoteSalesCounterpart | null | undefined): ISalesCounterpart | undefined {
  if (!client) {
    return undefined;
  }
  return {
    id: client.id,
    eliotCode: client?.eliotCode ?? undefined,
    label: client.label,
    countryResidenceCode: client.countryResidenceCode,
  } as ISalesCounterpart;
}

function mapClientResponse(response: ClientOrRelativesResponse): Partial<Defaults> {
  return {
    client: mapClientSalesCounterpart(response.client),
    salesId: response.selectedSale?.id ?? undefined,
  };
}
